import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronRight, Cloud, Satellite, Sprout, Leaf } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const JOURNEY_ABBR = {
  SAME_LIMIT_RENEWAL: 'Renewal',
  ENHANCED_LIMIT: 'Enhanced',
  NEW_FARMER: 'New',
};

const STATUS_STYLES = {
  DRAFT: { bg: '#f1f5f9', fg: '#475569', label: 'Draft' },
  SUBMITTED: { bg: '#fef3c7', fg: '#92400e', label: 'Sent for Review' },
  SCORED: { bg: '#dbeafe', fg: '#1e40af', label: 'Scored' },
  REVIEW_PENDING: { bg: '#fef9c3', fg: '#854d0e', label: 'With Officer' },
  APPROVED: { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
  REJECTED: { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  RETURNED: { bg: '#e0e7ff', fg: '#3730a3', label: 'Returned' },
};

const extIcons = [
  { key: 'weather', Icon: Cloud, color: '#2563eb', label: 'Weather' },
  { key: 'satellite', Icon: Satellite, color: '#7c3aed', label: 'NDVI' },
  { key: 'pm_kisan', Icon: Sprout, color: '#059669', label: 'PM-KISAN' },
  { key: 'soil_health', Icon: Leaf, color: '#b45309', label: 'Soil' },
];

const ExternalDataPills = ({ status = {} }) => (
  <div className="flex items-center gap-1" data-testid="ext-data-pills">
    {extIcons.map(({ key, Icon, color, label }) => {
      const ok = !!status[key];
      return (
        <div
          key={key}
          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium`}
          style={{
            background: ok ? `${color}15` : '#fef2f2',
            color: ok ? color : '#991b1b',
            border: ok ? 'none' : '1px solid #fecaca',
          }}
          title={`${label}: ${ok ? 'available' : 'missing'}`}
        >
          <Icon className="h-2.5 w-2.5" />
          {label}
        </div>
      );
    })}
  </div>
);

// Smart action CTA based on row state
const actionFor = (r) => {
  const status = r.assessment_status;
  const hasAllExt = r.external_data_status && Object.values(r.external_data_status).every(Boolean);
  const missingDocs = (r.missing_documents_count || 0) > 0;

  if (status === 'RETURNED') return { label: 'Correct & Resubmit', variant: 'destructive', primary: true };
  if (status === 'DRAFT') {
    if (r.score_readiness_status === 'READY') return { label: 'Submit for Scoring', variant: 'default', primary: true };
    if (missingDocs) return { label: 'Fix Missing Documents', variant: 'outline' };
    if (!hasAllExt) return { label: 'Refresh External Data', variant: 'outline' };
    return { label: 'Complete Profile', variant: 'outline' };
  }
  if (status === 'SUBMITTED') return { label: 'View', variant: 'ghost' };
  if (status === 'SCORED' || status === 'REVIEW_PENDING') return { label: 'View', variant: 'ghost' };
  if (status === 'APPROVED' || status === 'REJECTED') return { label: 'View', variant: 'ghost' };
  return { label: 'View', variant: 'outline' };
};

const AgentCaseTable = ({ rows = [], tab, loading }) => {
  const navigate = useNavigate();

  const continueUrl = (r) => {
    // Open assessment workflow so agent can keep editing
    return `/assessment/workflow/${r.journey_type}?farmerId=${r.farmer_id}`;
  };

  if (loading) {
    return <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-gray-500 text-sm">Loading...</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-12 text-center" data-testid="agent-empty-state">
        <div className="text-gray-400 text-sm">No cases in this section.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="agent-case-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2.5 font-semibold">Farmer</th>
              <th className="px-3 py-2.5 font-semibold">District</th>
              <th className="px-3 py-2.5 font-semibold">Journey</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
              <th className="px-3 py-2.5 font-semibold">Score Ready</th>
              <th className="px-3 py-2.5 font-semibold">Policy Ready</th>
              <th className="px-3 py-2.5 font-semibold">Docs</th>
              <th className="px-3 py-2.5 font-semibold">Ext. Data</th>
              <th className="px-3 py-2.5 font-semibold">Last Updated</th>
              <th className="px-3 py-2.5 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const statusStyle = STATUS_STYLES[r.assessment_status] || { bg: '#f1f5f9', fg: '#475569', label: r.assessment_status };
              const isReturned = r.assessment_status === 'RETURNED';
              return (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-slate-50" data-testid={`agent-row-${r.id}`}
                    style={{ borderLeft: isReturned ? '3px solid #dc2626' : '3px solid transparent' }}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-gray-900">{r.farmer_name}</div>
                    {r.aadhaar_last4 && <div className="text-[10px] text-gray-400 font-mono">**** {r.aadhaar_last4}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">{r.residential_district}</td>
                  <td className="px-3 py-2.5 text-gray-700">
                    <span className="font-medium">{JOURNEY_ABBR[r.journey_type] || r.journey_type}</span>
                    <span className="text-[10px] text-gray-400 ml-1">#{r.cycle_number}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge className="text-[10px] border-none" style={{ background: statusStyle.bg, color: statusStyle.fg }}>
                      {statusStyle.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge className={`text-[10px] border-none ${r.score_readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {r.score_readiness_status || 'N/A'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge className={`text-[10px] border-none ${r.policy_readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {r.policy_readiness_status || 'N/A'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    {(r.missing_documents_count || 0) > 0 ? (
                      <span className="text-red-700 font-semibold">{r.missing_documents_count} missing</span>
                    ) : (
                      <span className="text-green-700">Complete</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <ExternalDataPills status={r.external_data_status || {}} />
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-[11px]">
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + new Date(r.updated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {(() => {
                      const act = actionFor(r);
                      const style = act.primary && act.variant === 'destructive'
                        ? { background: '#dc2626', color: 'white' }
                        : act.primary
                        ? { background: '#1e40af', color: 'white' }
                        : {};
                      return (
                        <Button size="sm" variant={act.variant === 'destructive' || act.variant === 'default' ? 'default' : act.variant}
                          className="h-7 text-[11px]"
                          style={style}
                          onClick={() => navigate(continueUrl(r))}
                          data-testid={`agent-action-${r.id}`}
                        >
                          {act.label}
                          <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgentCaseTable;
